/**
 * PORTAS DE ENTRADA (H103, frente A) — o link que enche o grupo.
 *
 * ## O que estas telas nunca fazem
 *
 * 1. **Não escondem porta lotada.** Uma porta `ativa` com zero vagas está mandando gente para a
 *    página de espera **agora**, com o anúncio pago rodando. Isso vira alerta numa faixa fixa acima da
 *    lista, antes de qualquer outro número — é o único estado aqui que custa dinheiro por minuto.
 * 2. **Não deixam regenerar convite sem avisar.** Regenerar **revoga** o link atual: o anúncio que
 *    já está no ar para de funcionar. A confirmação diz isso com essas palavras.
 * 3. **Não escondem o trade-off da página de redirecionamento.** Pular entrega mais rápido e
 *    desliga Pixel, Analytics, webhook e a abertura no app. O rótulo diz; o servidor recusa a
 *    combinação; a `0088` recusa também.
 *
 * ## A família (spec 22)
 *
 * A lista é Consulta dentro do módulo de Grupos (`ModuloDeGrupos`, aba Automático › Portas de entrada) + `ListView`.
 * "Nova porta" (01/11) é a primária da aba: o nome (e o código, se quiser) num `Dialogo`; a porta nasce rascunho e
 * abre no detalhe, onde se escolhem os grupos. Até 01/11 o `POST /portas` existia e nenhuma tela o chamava.
 * A porta é Detalhe (`PaginaDeRegistro` + `Destaque` +
 * `Abas`), com os grupos na ordem em que enchem.
 */

import { useId, useState, type ReactNode } from "react";
import { EstadoDaFalha, chamar, falhaDe, identificadorLegivel, textos, useSessao } from "@hugi/ui";
import {
  Abas, CabecalhoDaTela, Carregando, Destaque, Dialogo, Dica, Erro, ListView, PaginaDeRegistro, PaginaInteira, Selo, SemPermissao, Vazio,
  type ColunaDaLista, type VistaDaLista,
} from "@hugi/ui/kit";
import { DoorOpen } from "lucide-react";
import { useLocation } from "wouter";
import { chavesDePortas, usePorta, usePortas, type GrupoDaPorta, type PortaNaLista } from "./dados.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import "./hugi-grupos.css";
import { ModoDoAutomatico, ModuloDeGrupos } from "./modulo-de-grupos.js";

// --- Vocabulário -----------------------------------------------------------------------------------

const SELO_POR_ESTADO: Record<PortaNaLista["estado"], { tom: "sucesso" | "neutro" | "atencao"; rotulo: string }> = {
  ativa: { tom: "sucesso", rotulo: "Distribuindo" },
  rascunho: { tom: "neutro", rotulo: "Rascunho" },
  encerrada: { tom: "atencao", rotulo: "Encerrada" },
};

const TITULO = "Portas de entrada";
const FRASE = "Um link só, colado no anúncio, que manda cada clique para o grupo que ainda tem vaga.";

const VISTAS: VistaDaLista<PortaNaLista>[] = [
  { id: "todas", rotulo: "Todas as portas", passa: () => true },
  { id: "ativas", rotulo: "Distribuindo", passa: (p) => p.estado === "ativa" },
  { id: "encerradas", rotulo: "Encerradas", passa: (p) => p.estado === "encerrada" },
];

/** O endereço público da porta, montado com o host que está servindo o painel. */
function enderecoDaPorta(codigo: string): string {
  return `${window.location.origin}/e/${codigo}`;
}

const lotada = (p: PortaNaLista) => p.estado === "ativa" && p.vagas === 0;

// --- Lista -----------------------------------------------------------------------------------------

export function PortasDeEntrada() {
  const consulta = usePortas();
  const [, navegar] = useLocation();
  const [copiado, setCopiado] = useState<string | null>(null);
  const sessao = useSessao();
  const podeCriar = sessao.permissoes.includes("manage_post");
  const [criando, setCriando] = useState(false);
  const primaria = (
    <Acao
      tom="primaria"
      desabilitado={!podeCriar}
      {...(podeCriar ? {} : { motivo: "Criar porta exige a permissão manage_post." })}
      onClick={() => setCriando(true)}
    >
      Nova porta
    </Acao>
  );
  const novaPorta = criando ? <NovaPorta aoFechar={() => setCriando(false)} aoCriar={(id) => navegar(`/portas/${id}`)} /> : null;

  const copiar = (codigo: string) => {
    void navigator.clipboard.writeText(enderecoDaPorta(codigo)).then(() => {
      setCopiado(codigo);
      setTimeout(() => setCopiado(null), 2000);
    });
  };

  if (consulta.isError) {
    return (
      <ModuloDeGrupos ativa="automatico" primaria={primaria}>
        <div className="v2-cartao"><Falha erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /></div>
        {novaPorta}
      </ModuloDeGrupos>
    );
  }

  const itens = consulta.data?.itens as PortaNaLista[] | undefined;
  const lotadas = (itens ?? []).filter(lotada);
  const nomesLotados = [...new Set(lotadas.map((p) => p.nome))].join(", ");

  const colunas: ColunaDaLista<PortaNaLista>[] = [
    { id: "nome", rotulo: "Porta", principal: true, link: true, celula: (p) => p.nome, dica: (p) => p.nome, ordenar: (p) => p.nome },
    { id: "endereco", rotulo: "Endereço", celula: (p) => <code className="hugi-mono">{enderecoDaPorta(p.codigo)}</code>, dica: (p) => enderecoDaPorta(p.codigo) },
    { id: "estado", rotulo: "Estado", celula: (p) => <Selo tom={SELO_POR_ESTADO[p.estado].tom}>{SELO_POR_ESTADO[p.estado].rotulo}</Selo>, ordenar: (p) => p.estado },
    { id: "grupos", rotulo: "Grupos", numerica: true, celula: (p) => p.grupos, ordenar: (p) => p.grupos },
    {
      id: "vagas", rotulo: "Vagas", numerica: true,
      celula: (p) => (lotada(p) ? <Selo tom="perigo">0 · porta cheia</Selo> : p.vagas),
      ordenar: (p) => p.vagas,
    },
    { id: "entrou", rotulo: "Entraram", numerica: true, celula: (p) => p.entrou, ordenar: (p) => p.entrou },
    { id: "semVaga", rotulo: "Sem vaga", numerica: true, celula: (p) => (p.semVaga > 0 ? <strong className="hugi-negativo">{p.semVaga}</strong> : p.semVaga), ordenar: (p) => p.semVaga },
    {
      id: "encerrada", rotulo: "Depois de encerrar", numerica: true,
      celula: (p) => (p.encerrada > 0 ? <span className="hugi-negativo"><strong>{p.encerrada}</strong> <small>anúncio ainda rodando</small></span> : p.encerrada),
      ordenar: (p) => p.encerrada,
    },
    {
      id: "acoes", rotulo: "Ações", acoes: true,
      celula: (p) => <Acao pequeno onClick={() => copiar(p.codigo)}>{copiado === p.codigo ? "Copiado" : "Copiar link"}</Acao>,
    },
  ];

  return (
    <ModuloDeGrupos ativa="automatico" primaria={primaria}>
      {lotadas.length > 0 ? (
        <p role="alert" className="hugi-grupos-faixa is-perigo" title={nomesLotados}>
          <span>
            {lotadas.length === 1
              ? `Todos os grupos desta porta estão cheios — ${lotadas[0]!.nome}. `
              : `Todos os grupos destas ${lotadas.length} portas estão cheios — ${nomesLotados}. `}
            Quem clicar no anúncio agora vai para a página de espera — acrescente grupos ou aumente o limite.
          </span>
        </p>
      ) : null}
      <ListView<PortaNaLista>
        titulo={TITULO}
        icone={<DoorOpen size={18} />}
        colunas={colunas}
        linhas={itens}
        chave={(p) => p.id}
        vistas={VISTAS}
        memoria="hugi:portas"
        abrir={{ href: (p) => `/portas/${p.id}`, aoAbrir: (p) => navegar(`/portas/${p.id}`) }}
        busca={{ campos: (p) => [p.nome, p.codigo], placeholder: "Nome ou código" }}
        contagem={(n) => (n === 1 ? "1 porta" : `${n} portas`)}
        vazio={{
          semNada: <><strong>Nenhuma porta de entrada ainda</strong>{" "}<span>Em Nova porta, crie uma, escolha os grupos na ordem em que devem encher e cole o link no anúncio. Quando um grupo lota, o próximo clique vai para o seguinte — e quando alguém sai, a vaga volta a ser usada.</span></>,
          semResultado: "Nenhuma porta nesta vista.",
        }}
        acoes={<><ModoDoAutomatico ativo="portas" /><Dica texto={FRASE} /></>}
      />
      {novaPorta}
    </ModuloDeGrupos>
  );
}

// --- Nova porta -----------------------------------------------------------------------------------

/** A porta nasce RASCUNHO, só com o nome (e o código do link, se quiser): os grupos se escolhem no detalhe. */
export function NovaPorta(props: { readonly aoFechar: () => void; readonly aoCriar: (id: string) => void }) {
  const clienteDeConsulta = useQueryClient();
  const [nome, setNome] = useState("");
  const [codigo, setCodigo] = useState("");
  const criar = useMutation({
    mutationFn: () => chamar<{ id: string; codigo: string }>("/api/painel/portas", { metodo: "POST", corpo: { nome: nome.trim(), ...(codigo.trim() ? { codigo: codigo.trim() } : {}) } }),
    onSuccess: (r) => { void clienteDeConsulta.invalidateQueries({ queryKey: chavesDePortas.lista() }); props.aoCriar(r.id); },
  });
  return (
    <Dialogo
      aberto
      aoFechar={props.aoFechar}
      titulo="Nova porta de entrada"
      rodape={
        <>
          <Acao onClick={props.aoFechar}>Cancelar</Acao>
          <Acao tom="primaria" desabilitado={!nome.trim() || criar.isPending} {...(!nome.trim() ? { motivo: "Dê um nome à porta." } : {})} onClick={() => criar.mutate()}>
            {criar.isPending ? "Criando…" : "Criar e escolher os grupos"}
          </Acao>
        </>
      }
    >
      <div className="hugi-grupos-formulario">
        <label className="hugi-grupos-campo">
          <span>Nome</span>
          <input className="campo" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Lançamento de novembro" aria-label="Nome da porta" />
        </label>
        <label className="hugi-grupos-campo">
          <span>Código do link <small>(opcional — vazio, o Hugi sugere)</small></span>
          <input className="campo hugi-mono" value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="novembro-vip" aria-label="Código do link" />
        </label>
        {criar.isError ? <p className="v2-erro" role="alert">{mensagemDe(criar.error)}</p> : null}
      </div>
    </Dialogo>
  );
}

// --- Detalhe ---------------------------------------------------------------------------------------

const ID_DO_TITULO = "hugi-porta-titulo";

export function PortaDetalhe(props: { readonly id: string }) {
  const consulta = usePorta(props.id);
  const lista = usePortas();
  const [, navegar] = useLocation();
  const clienteDeConsulta = useQueryClient();
  const [conexaoId, setConexaoId] = useState<string>("");
  const [regerando, setRegerando] = useState<GrupoDaPorta | null>(null);

  const puxar = useMutation({
    mutationFn: (p: { grupoId: string; regerar: boolean }) =>
      chamar<{ link: string }>(`/api/painel/portas/${props.id}/convite`, {
        metodo: "POST",
        corpo: { grupoId: p.grupoId, conexaoId, regerar: p.regerar },
      }),
    onSuccess: () => {
      void clienteDeConsulta.invalidateQueries({ queryKey: chavesDePortas.uma(props.id) });
    },
  });

  const voltar = () => navegar("/portas");

  if (consulta.isLoading || consulta.isError) {
    return (
      <PaginaInteira rotulo="Porta de entrada">
        <CabecalhoDaTela icone={<DoorOpen size={18} />} titulo="Porta de entrada" secundarias={<Acao onClick={voltar}>Todas as portas</Acao>} />
        <div className="v2-cartao">
          {consulta.isError ? <Falha erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} /> : <Carregando>Carregando a porta…</Carregando>}
        </div>
      </PaginaInteira>
    );
  }

  const porta = consulta.data?.porta ?? {};
  const grupos = consulta.data?.grupos ?? [];
  const conexoes = lista.data?.conexoes ?? [];
  const codigo = String(porta["codigo"] ?? "");
  const nome = String(porta["nome"] ?? "Porta de entrada");
  const pula = Boolean(porta["pular_redirecionamento"]);
  const comVaga = grupos.reduce((n, g) => n + (g.vagas > 0 && g.temConvite && g.gerenciado && g.grupoAtivo ? 1 : 0), 0);

  return (
    <PaginaDeRegistro
      idDoTitulo={ID_DO_TITULO}
      trilha={[{ rotulo: TITULO, href: "/portas", aoClicar: voltar }, { rotulo: nome }]}
      destaque={
        <Destaque
          icone={<DoorOpen size={18} />}
          tipo="Porta de entrada"
          titulo={nome}
          idDoTitulo={ID_DO_TITULO}
          campos={[
            { rotulo: "Endereço", valor: <code className="hugi-mono">{enderecoDaPorta(codigo)}</code>, dica: enderecoDaPorta(codigo) },
            { rotulo: "Grupos", valor: grupos.length },
            { rotulo: "Recebendo agora", valor: comVaga },
            { rotulo: "Redirecionamento", valor: pula ? "desligado" : "ligado" },
          ]}
          {...(pula
            ? {
                rodape: (
                  <p role="alert" className="hugi-alerta">
                    A página de redirecionamento está desligada nesta porta: o clique vai direto ao grupo, e Pixel,
                    Analytics, webhook e a abertura no aplicativo não acontecem.
                  </p>
                ),
              }
            : {})}
        />
      }
      principal={
        <>
          <Dialogo
            aberto={regerando !== null}
            aoFechar={() => setRegerando(null)}
            titulo={regerando ? `Gerar novo link para ${regerando.nome}?` : "Gerar novo link?"}
            rodape={
              <>
                <Acao onClick={() => setRegerando(null)}>Cancelar</Acao>
                <Acao tom="perigo" onClick={() => { const g = regerando; setRegerando(null); if (g) puxar.mutate({ grupoId: g.grupoId, regerar: true }); }}>Revogar e gerar outro</Acao>
              </>
            }
          >
            <p className="hugi-grupos-aviso">Isto revoga o link atual — o anúncio no ar para de funcionar.</p>
          </Dialogo>
        <Abas rotulo="Seções da porta" abas={[{ id: "grupos", rotulo: "Grupos, na ordem em que enchem", contador: grupos.length }]}>
          {() => (
            <div className="hugi-corpo-registro">
              <div className="hugi-barra v2-cartao">
                <label>
                  <span>Número que administra</span>
                  <select className="campo" value={conexaoId} onChange={(e) => setConexaoId(e.target.value)}>
                    <option value="">escolha…</option>
                    {conexoes.map((c) => (
                      <option key={c.id} value={c.id}>{c.apelido ?? (c.numeroExibicao ? identificadorLegivel(c.numeroExibicao) : c.id)}</option>
                    ))}
                  </select>
                </label>
                {puxar.isError ? <p className="hugi-barra-aviso is-ruim" role="alert">{mensagemDe(puxar.error)}</p> : null}
              </div>


              {grupos.length === 0 ? (
                <Vazio><strong>Nenhum grupo nesta porta</strong>{" "}<span>Escolha os grupos em Grupos › Seus grupos e acrescente-os aqui. A ordem é a ordem em que eles enchem.</span></Vazio>
              ) : (
                <div className="v2-tabela-rolagem">
                  <table className="v2-tabela">
                    <thead>
                      <tr><th scope="col">Grupo</th><th scope="col" className="is-num">Ocupação</th><th scope="col">Situação</th><th scope="col">Convite</th><th scope="col">Ação</th></tr>
                    </thead>
                    <tbody>
                      {grupos.map((g) => (
                        <LinhaDoGrupo
                          key={g.grupoId}
                          grupo={g}
                          podePuxar={Boolean(conexaoId)}
                          aoPegar={() => puxar.mutate({ grupoId: g.grupoId, regerar: false })}
                          aoRegerar={() => setRegerando(g)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Abas>
        </>
      }
    />
  );
}

function LinhaDoGrupo(props: {
  readonly grupo: GrupoDaPorta;
  readonly podePuxar: boolean;
  readonly aoPegar: () => void;
  readonly aoRegerar: () => void;
}) {
  const g = props.grupo;

  // Cada impedimento tem uma frase própria: eles se consertam de maneiras diferentes, e "não está
  // recebendo" sem dizer por quê é o que faz alguém mexer na coisa errada.
  const impedimento = !g.grupoAtivo
    ? "Este grupo sumiu do WhatsApp na última sincronização."
    : !g.gerenciado
      ? "Este grupo está fora do plano — o teto de grupos gerenciados não o inclui."
      : !g.temConvite
        ? "Sem link de convite: este grupo é pulado na distribuição."
        : g.vagas === 0
          ? "Cheio. O próximo clique vai para o grupo seguinte."
          : null;
  const motivo = props.podePuxar ? {} : { motivo: "Escolha o número que administra o grupo." };

  return (
    <tr>
      <td className="is-titulo"><strong>{g.nome}</strong></td>
      <td className="is-num">{g.participantes} de {g.limite} · {g.vagas} vaga{g.vagas === 1 ? "" : "s"}</td>
      <td className="is-quebra">{impedimento ? <small>{impedimento}</small> : <span className="is-ausente">recebendo</span>}</td>
      <td>{g.temConvite ? <Selo tom="sucesso">Com convite</Selo> : <Selo tom="perigo">Sem convite</Selo>}</td>
      <td className="is-acoes">
        {!g.temConvite ? (
          <Acao pequeno desabilitado={!props.podePuxar} {...motivo} onClick={props.aoPegar}>Pegar link</Acao>
        ) : (
          <Acao pequeno desabilitado={!props.podePuxar} {...motivo} onClick={props.aoRegerar}>Gerar novo link</Acao>
        )}
      </td>
    </tr>
  );
}

// --- Miúdos da tela ---------------------------------------------------------------------------------

/** A frase do servidor quando ele deu uma; sem ela, a genérica. */
function mensagemDe(erro: unknown): string {
  return falhaDe(erro)?.erro.mensagem ?? textos.estados.erro.corpo;
}

/** O estado de falha da área, com os estados do kit (e o da casca quando é caso comercial ou de versão). */
function Falha(props: { readonly erro: unknown; readonly aoTentarDeNovo?: () => void }) {
  const f = falhaDe(props.erro);
  if (f?.tipo === "sem_permissao") return <SemPermissao>{f.permissao ? textos.estados.semPermissao.qual(f.permissao) : textos.estados.semPermissao.semNome}</SemPermissao>;
  if (f?.tipo === "sem_entitlement" || f?.tipo === "versao_incompativel") return <EstadoDaFalha falha={f} />;
  return <Erro {...(props.aoTentarDeNovo ? { aoTentarDeNovo: props.aoTentarDeNovo } : {})}>{f?.erro.mensagem ?? textos.estados.erro.corpo}</Erro>;
}

/** Botão do kit; desabilitado, diz o motivo (no `title` e ligado por `aria-describedby`). */
function Acao(props: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly tom?: "primaria" | "perigo";
  readonly pequeno?: boolean;
  readonly desabilitado?: boolean;
  readonly motivo?: string;
}) {
  const id = useId();
  const comMotivo = Boolean(props.desabilitado && props.motivo);
  return (
    <>
      <button
        type="button"
        className={`v2-botao${props.tom === "primaria" ? " is-primario" : props.tom === "perigo" ? " is-perigo" : ""}${props.pequeno ? " is-pequeno" : ""}`}
        disabled={props.desabilitado}
        title={comMotivo ? props.motivo : undefined}
        aria-describedby={comMotivo ? id : undefined}
        onClick={props.onClick}
      >
        {props.children}
      </button>
      {comMotivo ? <span id={id} className="v2-sr">{props.motivo}</span> : null}
    </>
  );
}
